export const DEFAULT_TAB = "reflection-report";

export const TAB_PARAM_MAP: Record<string, string> = {
	analysis: "checkin-analysis",
};

export const STATUS_COLORS = {
	completed:
		"bg-green-100 text-green-800 border-green-200 dark:bg-green-800 dark:text-green-300 dark:border-green-700",
	in_progress:
		"bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-800 dark:text-blue-300 dark:border-blue-700",
	not_started:
		"bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700",
} as const;

export const STATUS_LABELS: Record<string, string> = {
	not_started: "Not Started",
	in_progress: "In Progress",
	completed: "Completed",
};

export const PRINT_STYLES = `
  /* ===========================================
     SCREEN STYLES - Hide print-only content
     =========================================== */
  #print-combined {
    display: none;
  }

  /* ===========================================
     PRINT STYLES
     =========================================== */
  @media print {
    /* Force color printing */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    @page {
      margin: 1cm 1.5cm;
      size: A4;
    }

    /* Force light mode */
    html, body {
      background: white !important;
      color: #1e293b !important;
      font-size: 10pt !important;
      line-height: 1.5 !important;
    }

    /* Hide UI elements that shouldn't print */
    .no-print,
    button:not(.print-keep),
    [role="tablist"],
    nav,
    .print-hide {
      display: none !important;
    }

    /* Hide summary card (duplicate of current state) */
    .print-hide-summary {
      display: none !important;
    }

    /* Hide status badges, priority, and count from corrective actions */
    .print-hide-status,
    .print-hide-priority,
    .print-hide-count {
      display: none !important;
    }

    /* Remove colored left borders from analysis observations */
    .print-remove-border[class*="border-l-4"] {
      border-left: none !important;
      border-left-width: 0 !important;
    }

    /* Remove blue borders from recommended actions and signal patterns */
    .print-remove-border[class*="border-blue"],
    .print-remove-border.border-blue-200,
    .print-remove-border[class*="border-blue-200"] {
      border-color: #e2e8f0 !important;
      border-left-color: #e2e8f0 !important;
      border-right-color: #e2e8f0 !important;
      border-top-color: #e2e8f0 !important;
      border-bottom-color: #e2e8f0 !important;
    }

    /* Hide signals count in Areas for Attention */
    .print-hide-signals-count,
    .print-hide-signals-separator {
      display: none !important;
    }

    /* Ensure active tab panel is visible (override Radix hidden attribute) */
    [role="tabpanel"][data-state="active"] {
      display: block !important;
    }

    /* Keep inactive tabs hidden unless we're combining */
    [role="tabpanel"][data-state="inactive"]:not(.print-visible) {
      display: none !important;
    }

    /* ===== COMBINED PRINT MODE ===== */
    /* When .print-hidden is on tabs wrapper, hide it */
    #tabs-wrapper.print-hidden {
      display: none !important;
    }

    /* When .print-visible is on combined content, show it */
    #print-combined.print-visible {
      display: block !important;
    }

    /* Analysis section styling */
    .print-analysis-section {
      page-break-before: always !important;
      margin-top: 2rem !important;
      padding-top: 1rem !important;
      border-top: 3px solid #3b82f6 !important;
    }

    /* ===== TEXT COLORS ===== */
    /* Force readable text on all elements */
    h1, h2, h3, h4, h5, h6 {
      color: #0f172a !important;
    }

    p, li, span, div, td, th, label {
      color: #334155 !important;
    }

    /* Fix dark mode text classes */
    .text-foreground,
    [class*="text-foreground"] {
      color: #1e293b !important;
    }

    .text-muted-foreground,
    [class*="text-muted-foreground"] {
      color: #64748b !important;
    }

    /* ===== CARD STYLING ===== */
    [class*="Card"],
    [class*="card"] {
      background: white !important;
      border: 1px solid #e2e8f0 !important;
      border-radius: 0.5rem !important;
      box-shadow: none !important;
      margin-bottom: 1rem !important;
      page-break-inside: avoid !important;
    }

    /* ===== BACKGROUNDS ===== */
    /* Force light backgrounds */
    [class*="dark:bg-slate"],
    [class*="dark:bg-gray"],
    [class*="bg-slate-800"],
    [class*="bg-gray-800"] {
      background: white !important;
    }

    /* Force white backgrounds on all colored background tints (corrective actions, etc.) */
    [class*="bg-green-50"],
    [class*="bg-green-50/50"],
    [class*="bg-red-50"],
    [class*="bg-red-50/50"],
    [class*="bg-blue-50"],
    [class*="bg-blue-50/50"],
    [class*="bg-orange-50"],
    [class*="bg-orange-50/50"],
    [class*="dark:bg-green-900"],
    [class*="dark:bg-green-900/10"],
    [class*="dark:bg-red-900"],
    [class*="dark:bg-red-900/10"],
    [class*="dark:bg-blue-900"],
    [class*="dark:bg-blue-900/10"] {
      background: white !important;
    }

    /* Remove or minimize shadows that might create darker appearance */
    [class*="shadow-md"],
    [class*="shadow-lg"],
    [class*="shadow-xl"] {
      box-shadow: none !important;
    }

    /* Ensure all nested boxes inside cards have white backgrounds */
    [class*="Card"] > div > div,
    [class*="card"] > div > div,
    [class*="CardContent"] > div > div {
      background: white !important;
      box-shadow: none !important;
    }

    /* Keep gradient backgrounds */
    [class*="bg-gradient-to-br"][class*="from-blue-500"] {
      background: linear-gradient(to bottom right, #3b82f6, #4f46e5) !important;
    }
    [class*="bg-gradient-to-br"][class*="from-green-500"] {
      background: linear-gradient(to bottom right, #22c55e, #10b981) !important;
    }
    [class*="bg-gradient-to-br"][class*="from-red-500"] {
      background: linear-gradient(to bottom right, #ef4444, #f97316) !important;
    }

    /* ===== ICONS ===== */
    svg {
      width: 1rem !important;
      height: 1rem !important;
    }

    /* White icons on colored backgrounds */
    [class*="bg-gradient-to-br"] svg {
      color: white !important;
      stroke: white !important;
    }

    /* ===== PRESERVE COLORED TEXT ===== */
    [class*="text-green-600"], [class*="text-green-700"] {
      color: #16a34a !important;
    }
    [class*="text-red-600"], [class*="text-red-700"] {
      color: #dc2626 !important;
    }
    [class*="text-blue-600"], [class*="text-blue-700"] {
      color: #2563eb !important;
    }

    /* ===== BORDERS ===== */
    /* Remove colored borders - make them neutral gray */
    [class*="border-green"],
    [class*="border-red"],
    [class*="border-orange"] {
      border-color: #e2e8f0 !important;
    }
    [class*="border-blue"] { border-color: #3b82f6 !important; }

    /* Make signals icon red to match contributing factors */
    .print-signals-icon {
      background: linear-gradient(to bottom right, #ef4444, #f97316) !important;
    }

    /* ===== LAYOUT ===== */
    .container {
      max-width: 100% !important;
      padding: 0 !important;
    }

    .space-y-6 > * {
      margin-bottom: 1rem !important;
    }

    /* ===== PRINT HEADER/FOOTER ===== */
    .print-header {
      display: block !important;
      margin-bottom: 1rem !important;
      border-bottom: 2px solid #e2e8f0 !important;
    }

    /* Make print title twice as large */
    .print-header h1 {
      font-size: 2.5rem !important;
      line-height: 1.2 !important;
    }

    .print-footer {
      display: block !important;
      margin-top: 1rem !important;
      border-top: 1px solid #e2e8f0 !important;
      font-size: 0.625rem !important;
      color: #64748b !important;
      text-align: center !important;
    }
  }
`;
